import { useState } from 'react'
import {
  draftsToPendingAttachments,
  type PendingAttachmentLike,
} from '../../../../hooks/useAttachmentUpload'
import { useSystemIssueAttachmentUpload } from '../../hooks/useSystemIssueAttachmentUpload'
import {
  COMPOSER_TEXTAREA_CLASS,
  ComposerFrame,
  pasteFilesHandler,
  useFreshTextarea,
} from '../../../../components/chat/ComposerFrame'

interface IssueMessageEditorProps {
  disabled?: boolean
  disabledReason?: string
  isSending: boolean
  onSend: (content: string, attachments: PendingAttachmentLike[]) => void
  /** Painel estreito ("Relatar problema"). */
  compact?: boolean
}

/**
 * Editor da conversa do chamado: a mesma moldura do de Ocorrências, com
 * caixa simples (sem @menção — colegas entram por "Marcar colega") e anexo
 * só de imagem.
 */
export function IssueMessageEditor({
  disabled,
  disabledReason,
  isSending,
  onSend,
  compact = false,
}: IssueMessageEditorProps) {
  const [draft, setDraft] = useState('')
  const field = useFreshTextarea()
  const {
    drafts,
    addFiles,
    removeDraft,
    reset: resetAttachments,
    isUploading,
    hasErrors,
  } = useSystemIssueAttachmentUpload()

  const handleSend = () => {
    const trimmed = draft.trim()
    const attachments = draftsToPendingAttachments(drafts)
    if ((!trimmed && attachments.length === 0) || isSending || disabled) return
    if (isUploading) return
    onSend(trimmed, attachments)
    setDraft('')
    field.renew()
    resetAttachments()
  }

  return (
    <ComposerFrame
      drafts={drafts}
      onAddFiles={addFiles}
      onRemoveDraft={removeDraft}
      accept="image/*"
      hasText={draft.trim().length > 0}
      isSending={isSending}
      isUploading={isUploading}
      hasErrors={hasErrors}
      onSend={handleSend}
      disabledReason={disabled ? (disabledReason ?? 'Editor desabilitado.') : null}
      compact={compact}
    >
      <textarea
        key={field.key}
        autoFocus={field.autoFocus}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onPaste={pasteFilesHandler(addFiles)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
          }
        }}
        rows={3}
        placeholder="Escreva uma mensagem... arraste ou cole com Ctrl+V para anexar"
        aria-label="Mensagem"
        className={COMPOSER_TEXTAREA_CLASS}
      />
    </ComposerFrame>
  )
}
